// Code-action: "Add missing table to FROM" — Parse + folding helpers.
//
// Split out of addMissingTableToFrom.ts (behavior-neutral).

import type { ParseOptions, SqlEngine } from '../../../sql-grammar/model';
import {
  buildParsedDocumentFromSource,
  getDocumentStructure,
} from '../../../sql-grammar/document';
import type { ParsedBuffer } from '../../../sql-resolver/types';
import type { DatabaseExplorerNode } from '../../../explorer/models/DatabaseTreeModels';
import { useDatabaseStore } from '../../../database-objects/store/databaseStore';
import { extendCursorToIdentEnd } from '../../go-to/cursorIdent';

export function parseBuffer(source: string, options: ParseOptions): ParsedBuffer | null {
  try {
    const parsedDocument = buildParsedDocumentFromSource(source, options);
    const documentStructure = getDocumentStructure(parsedDocument, 'add-missing-table-to-from');
    return { parsedDocument, documentStructure };
  } catch {
    return null;
  }
}

export function defaultFoldingForEngine(engine: SqlEngine): ParseOptions['identifierFolding'] {
  if (engine === 'postgres') return 'lower';
  if (engine === 'oracle') return 'upper';
  return 'case-insensitive';
}

export function readLiveTree(): readonly DatabaseExplorerNode[] {
  return useDatabaseStore.getState().treeData;
}

// Extend the cursor offset to the end of a dotted chain — e.g., cursor on
// `orders` in `orders.total` returns the offset just past `total`. This
// lets the resolver see both parts when the user cursors on the prefix.
export function extendCursorThroughChain(source: string, offset: number): number {
  let pos = extendCursorToIdentEnd(source, offset);
  while (pos < source.length && source[pos] === '.') {
    const nextStart = pos + 1;
    if (nextStart >= source.length) break;
    const ch = source[nextStart];
    if (ch === '"' || ch === '`' || ch === '[') {
      // Quoted next part — let extendCursorToIdentEnd handle it.
      pos = extendCursorToIdentEnd(source, nextStart);
      if (pos === nextStart) break;
      continue;
    }
    if (!/[A-Za-z0-9_$]/.test(ch)) break;
    let k = nextStart;
    while (k < source.length && /[A-Za-z0-9_$]/.test(source[k])) k++;
    pos = k;
  }
  return pos;
}
